/**
 * ENG-8116: per-minute occupancy DURATIONS, accumulated host-side.
 *
 * ## Why this exists
 *
 * The busy/idle pipeline used to measure occupancy by counting probe arrivals:
 * the manager reported "last activity was N seconds ago", the API stored that
 * instant, and `sample_agent_activity()` inserted one row with a CONSTANT
 * `bucket_seconds = 60` for every agent whose instant was fresh within 60s.
 *
 * That makes billed minutes a function of PROBE CADENCE rather than of work.
 * Measured on the prod fleet (56 agents, 6h of `PaneActivityAgeSeconds`
 * SampleCount): median cadence 87s, mean 108s, p90 182s — and **zero** agents
 * inside the 60s window. Every sampler tick landing more than 60s after the
 * last probe write read IDLE for an agent that was working: ~31% of buckets
 * lost at the median, ~67% at p90.
 *
 * Re-tuning the probe interval cannot fix this. Effective probe gap is
 * `T = s·⌈(I+1)/s⌉` where `s` is poll-cycle spacing, so `T >= s` always — and
 * `s` is itself 29s–180s on real hosts. On the p90 host no value of `I`
 * produces a sub-60s cadence.
 *
 * So stop counting arrivals and start reporting duration. The manager knows
 * exactly when each agent was occupied, so it accrues that time into wall-clock
 * minute buckets here and reports the buckets. The API stamps the MEASURED
 * seconds into `bucket_seconds` — a column that has existed since ENG-7549 for
 * precisely this purpose and has only ever been fed a constant.
 *
 * The result is cadence-INDEPENDENT by construction: a slow host posts less
 * often and reports proportionally more, so total billed minutes are invariant
 * to poll duration, backoff, POST failures, and future runtimes. It retires the
 * unwritten `probe_gap < freshness_window` invariant instead of re-tuning it.
 *
 * ## What this is not
 *
 * Occupancy only. Nothing here may reach liveness — ENG-8090 split the two at
 * the column level (`last_busy_activity_at` vs `last_known_good_at`) precisely
 * because a serve wedged mid-turn is occupied but NOT alive, and routing
 * occupancy into `lastAliveMs` would hide the ENG-8058 wedge class.
 */

/** Wall-clock bucket width. Matches `agent_activity_samples.bucket_seconds`. */
export const BUCKET_MS = 60_000;

/**
 * Ceiling on a SINGLE accrual span. Occupancy is accrued incrementally (every
 * probe pass ticks in-flight work), so a span this long means the manager
 * itself was stalled — a laptop suspend, a stop-the-world GC, an SSM session
 * that froze the process — not an agent working. Without the cap, a host that
 * slept for eight hours with one turn in flight would wake up and bill eight
 * hours. Long legitimate turns are unaffected: they accrue in small pieces as
 * the ticks land.
 */
export const MAX_ACCRUAL_SPAN_MS = 10 * 60_000;

/**
 * Retained closed buckets per agent while POSTs are failing. Occupancy is
 * re-credited on a failed POST (see the drain contract below), so without a
 * bound a multi-day API outage would grow this map without limit. Four hours
 * of buckets is far longer than any realistic outage the manager survives, and
 * dropping the OLDEST is the right eviction: recent occupancy is the part still
 * inside the sampler's retention horizon and therefore still billable.
 */
export const MAX_RETAINED_BUCKETS = 240;

/**
 * Billing policy: a wall-clock minute containing ANY real occupancy is billed
 * as a whole minute.
 *
 * The ledger measures true milliseconds internally — that is what makes it
 * testable and what makes the cadence-independence property provable — but a
 * partial minute is reported as 60 seconds. Deliberate, and chosen over
 * reporting true seconds:
 *
 *   - It is the semantic the pipeline already had. `bucket_seconds` was ALWAYS
 *     60; the defect was never the constant, it was that a minute only got a
 *     row if a probe happened to land inside the freshness window (31%-67% of
 *     them did not). Rounding up within a minute keeps the entire fix and
 *     leaves every downstream consumer seeing the shape it always saw.
 *   - It rounds in the customer-charged direction on a meter, which is the
 *     explicit product decision here, and it is bounded by REAL measured
 *     activity rather than by inference.
 *   - It is explainable without reference to any implementation detail: a
 *     minute in which your agent did something is a billed minute.
 *
 * For a continuously busy agent this changes nothing (already 60). It shows up
 * on the partial minutes at the edges of work episodes.
 */
export const BILL_WHOLE_MINUTES = true;

/**
 * Occupancy below this in a minute is not billed at all.
 *
 * Required BECAUSE of the whole-minute policy above. A gate decline occupies an
 * agent for the microseconds the sender check takes; without a floor, rounding
 * that up would bill a full minute for every refused message, and a chatty
 * channel that filters heavily would manufacture billable minutes out of an
 * idle agent. 100ms separates cleanly: a decline is a synchronous policy check,
 * while any real turn crosses the network.
 */
export const MIN_BILLABLE_MS = 100;

/** One minute of measured occupancy, as reported on the wire. */
export interface BusyBucket {
  /** ISO-8601 start of the wall-clock minute. */
  bucket: string;
  /** Occupied seconds within that minute, 1..60. */
  seconds: number;
}

interface AgentLedger {
  /** bucketStartMs -> occupied milliseconds within that bucket. */
  buckets: Map<number, number>;
  /**
   * When the agent became occupied, or null when idle. Accrual is deferred
   * until a tick so an in-flight turn keeps filling buckets while it runs
   * rather than crediting everything at resolution — a 5-minute turn must mark
   * five buckets, not one.
   */
  openSince: number | null;
}

export function bucketStartMs(atMs: number): number {
  return Math.floor(atMs / BUCKET_MS) * BUCKET_MS;
}

export class BusyBucketLedger {
  private readonly state = new Map<string, AgentLedger>();

  private entry(codeName: string): AgentLedger {
    let cur = this.state.get(codeName);
    if (!cur) {
      cur = { buckets: new Map(), openSince: null };
      this.state.set(codeName, cur);
    }
    return cur;
  }

  /**
   * Credit occupancy for the span [fromMs, toMs), splitting it across every
   * minute bucket it covers. Safe to call with any ordering — a reversed or
   * zero-length span is a no-op rather than a negative credit.
   */
  accrueSpan(codeName: string, fromMs: number, toMs: number): void {
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return;
    if (toMs <= fromMs) return;

    // Clamp before splitting, so a stalled manager cannot bill its downtime.
    const start = Math.max(fromMs, toMs - MAX_ACCRUAL_SPAN_MS);
    const led = this.entry(codeName);

    for (let b = bucketStartMs(start); b < toMs; b += BUCKET_MS) {
      const overlap = Math.min(toMs, b + BUCKET_MS) - Math.max(start, b);
      if (overlap <= 0) continue;
      const prev = led.buckets.get(b) ?? 0;
      // A bucket cannot hold more than a minute of occupancy, however the
      // spans arrive (concurrent turns on one agent overlap by design).
      led.buckets.set(b, Math.min(BUCKET_MS, prev + overlap));
    }
    this.evict(led);
  }

  /** The agent started doing work. Idempotent while already occupied. */
  open(codeName: string, atMs: number): void {
    const led = this.entry(codeName);
    if (led.openSince == null) led.openSince = atMs;
  }

  /**
   * Accrue everything owed up to `nowMs` WITHOUT ending the occupancy. This is
   * what makes a long turn fill every bucket it spans: each probe pass ticks,
   * banking the elapsed slice and moving the watermark forward.
   */
  tick(codeName: string, nowMs: number): void {
    const led = this.state.get(codeName);
    if (!led || led.openSince == null) return;
    // Forward-only watermark, as defence in depth against an NTP step-back on a
    // long-running host. `accrueSpan` already no-ops on the reversed span
    // itself, but without this the watermark would move BACKWARDS and the next
    // forward tick would re-accrue an already-credited interval.
    //
    // Honest scope note (CodeRabbit raised this as double-billing, PR #3752):
    // that consequence is currently neutralised three times over — the
    // per-bucket cap makes re-accrual idempotent on full buckets,
    // BILL_WHOLE_MINUTES rounds partial differences away, and the API upserts
    // on (agent_id, sampled_at) so a re-reported minute cannot charge twice. I
    // could not construct a case where removing this line changes a billed
    // number, and deliberately did NOT add a test that would pass either way.
    // It stays because it is correct and free, and because it is the only one
    // of those four mechanisms that lives at the point of the mistake — the
    // other three are downstream and could each be changed independently.
    if (nowMs <= led.openSince) return;
    this.accrueSpan(codeName, led.openSince, nowMs);
    led.openSince = nowMs;
  }

  /** The agent stopped doing work. Banks the final slice. */
  close(codeName: string, atMs: number): void {
    const led = this.state.get(codeName);
    if (!led || led.openSince == null) return;
    this.accrueSpan(codeName, led.openSince, atMs);
    led.openSince = null;
  }

  /**
   * Take every CLOSED bucket for reporting, removing it from the ledger.
   *
   * Only closed buckets — a bucket whose minute has not yet elapsed is still
   * accruing, and reporting it early would send a partial value that the next
   * drain would have to correct. The open bucket stays and is drained once it
   * closes.
   *
   * DESTRUCTIVE, deliberately: this is the same read-and-reset contract as the
   * watchdog give-up counters, and it carries the same obligation — the caller
   * MUST `credit()` the result back if the POST fails, or a transient 5xx
   * permanently deletes billable occupancy that cannot be reconstructed.
   */
  drainClosed(codeName: string, nowMs: number): BusyBucket[] {
    const led = this.state.get(codeName);
    if (!led) return [];
    // Bank in-flight work first, so an agent occupied across the whole interval
    // reports it rather than holding it until the turn happens to resolve.
    this.tick(codeName, nowMs);

    const openBucket = bucketStartMs(nowMs);
    const out: BusyBucket[] = [];
    for (const [b, ms] of [...led.buckets].sort((x, y) => x[0] - y[0])) {
      if (b >= openBucket) continue;
      led.buckets.delete(b);
      // Below the floor is noise (a gate decline), not work — see
      // MIN_BILLABLE_MS. Above it, the whole minute is billed.
      if (ms < MIN_BILLABLE_MS) continue;
      const seconds = BILL_WHOLE_MINUTES ? 60 : Math.min(60, Math.ceil(ms / 1000));
      out.push({ bucket: new Date(b).toISOString(), seconds });
    }
    return out;
  }

  /**
   * Put drained buckets back after a failed POST. Merges rather than replaces,
   * so occupancy accrued since the drain is preserved.
   */
  credit(codeName: string, buckets: BusyBucket[]): void {
    if (buckets.length === 0) return;
    const led = this.entry(codeName);
    for (const b of buckets) {
      const at = Date.parse(b.bucket);
      if (!Number.isFinite(at)) continue;
      const key = bucketStartMs(at);
      const prev = led.buckets.get(key) ?? 0;
      led.buckets.set(key, Math.min(BUCKET_MS, prev + b.seconds * 1000));
    }
    this.evict(led);
  }

  /** Agents currently holding reportable state. */
  trackedAgents(): string[] {
    return [...this.state.keys()];
  }

  /**
   * Drop everything for an agent. Called from the same teardown that resets
   * turn health, so a fresh session is not born holding the dead one's
   * occupancy.
   */
  reset(codeName: string): void {
    this.state.delete(codeName);
  }

  private evict(led: AgentLedger): void {
    if (led.buckets.size <= MAX_RETAINED_BUCKETS) return;
    const ordered = [...led.buckets.keys()].sort((a, b) => a - b);
    for (const k of ordered.slice(0, led.buckets.size - MAX_RETAINED_BUCKETS)) {
      led.buckets.delete(k);
    }
  }
}

/**
 * Process-wide ledger. Both occupancy producers write here — opencode from its
 * turn lifecycle (exact spans) and Claude Code from `pane.log` mtime deltas
 * (sampled) — so the probe has a single place to drain regardless of runtime,
 * and adding a future runtime means adding a producer rather than another
 * column and another sampler branch.
 */
export const sharedBusyBuckets = new BusyBucketLedger();
